import React, { useEffect, useState } from 'react';
import { API_URL } from '../config/api';
import { toast } from 'react-toastify';
import './ScriptAnalysisPanel.css';

interface ChecklistResult {
  id: string;
  description: string;
  passed: boolean;
  aiComment: string;
}

interface AnalysisResult {
  checklistResults: ChecklistResult[];
  overallScore: number;
  suggestedFeedback: string;
}

interface ScriptAnalysisPanelProps {
  collaborationId: string;
  onFeedbackGenerated: (feedback: string) => void;
}

const ScriptAnalysisPanel: React.FC<ScriptAnalysisPanelProps> = ({
  collaborationId,
  onFeedbackGenerated
}) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [hasAnalyzed, setHasAnalyzed] = useState(false);
  // Checklist state (integrated from modal)
  const [checklistItems, setChecklistItems] = useState<Array<{ _id: string; description: string; order: number }>>([]);
  const [loadingChecklist, setLoadingChecklist] = useState(false);
  const [newDescription, setNewDescription] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');

  useEffect(() => {
    fetchChecklistItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchChecklistItems = async () => {
    setLoadingChecklist(true);
    try {
      const response = await fetch(`${API_URL}/api/script-checklist`);
      const data = await response.json();

      if (data.success) {
        setChecklistItems(data.data);
      } else {
        toast.error('Failed to fetch checklist items');
      }
    } catch (error) {
      console.error('Error fetching checklist:', error);
      toast.error('Error fetching checklist items');
    } finally {
      setLoadingChecklist(false);
    }
  };

  const handleAddChecklistItem = async () => {
    if (!newDescription.trim()) {
      toast.error('Please enter a description');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/script-checklist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: newDescription })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Added');
        setChecklistItems((prev) => [...prev, data.data]);
        setNewDescription('');
      } else {
        toast.error(data.error || 'Failed to add');
      }
    } catch (error) {
      console.error('Error adding:', error);
      toast.error('Error adding description');
    }
  };

  const handleUpdateChecklistItem = async (id: string) => {
    if (!editingText.trim()) {
      toast.error('Description cannot be empty');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/script-checklist/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: editingText })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Updated');
        await fetchChecklistItems();
        setEditingId(null);
        setEditingText('');
      } else {
        toast.error(data.error || 'Failed to update');
      }
    } catch (error) {
      console.error('Error updating:', error);
      toast.error('Error updating description');
    }
  };

  const handleDeleteChecklistItem = async (id: string) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      const response = await fetch(`${API_URL}/api/script-checklist/${id}`, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        toast.success('Deleted');
        setChecklistItems((prev) => prev.filter((i) => i._id !== id));
      } else {
        toast.error(data.error || 'Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Error deleting item');
    }
  };

  const analyzeScript = async () => {
    setIsAnalyzing(true);
    try {
      const response = await fetch(`${API_URL}/api/collaborations/${collaborationId}/analyze-script`, {
        method: 'POST'
      });

      const data = await response.json();

      if (data.success && data.data) {
        // Validate the data structure
        const result = data.data;
        if (result.checklistResults && Array.isArray(result.checklistResults)) {
          // Additional validation to ensure data integrity
          const validatedResult = {
            checklistResults: result.checklistResults.map((item: any) => ({
              id: item.id || Math.random().toString(),
              description: item.description || 'Unknown requirement',
              passed: Boolean(item.passed),
              aiComment: item.aiComment || 'No analysis available'
            })),
            overallScore: Number(result.overallScore) || 0,
            suggestedFeedback: typeof result.suggestedFeedback === 'string'
              ? result.suggestedFeedback
              : 'Please review the script based on the checklist requirements.'
          };

          setAnalysisResult(validatedResult);
          setHasAnalyzed(true);
          toast.success('Script analyzed successfully');
        } else {
          console.error('Invalid analysis result structure:', result);
          toast.error('Analysis completed but results are invalid');
        }
      } else {
        toast.error(data.error || 'Failed to analyze script');
      }
    } catch (error) {
      console.error('Error analyzing script:', error);
      toast.error('Error analyzing script');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const generateFeedback = () => {
    if (analysisResult && analysisResult.suggestedFeedback) {
      // The feedback should already be clean from our validation
      const feedback = analysisResult.suggestedFeedback;
      onFeedbackGenerated(feedback);
      toast.success('Feedback added to text area');
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const getCheckIcon = (passed: boolean) => {
    return passed ? '✅' : '❌';
  };

  return (
    <div className="script-analysis-panel">
      {/* Checklist Management */}
      <div className="checklist-section">
        <div className="checklist-header">
          <h3>📋 Script Checklist</h3>
          {loadingChecklist && <span className="checklist-loading">Loading...</span>}
        </div>
        <div className="checklist-items">
          {checklistItems.length === 0 && !loadingChecklist && (
            <div className="empty-checklist">No checklist items yet</div>
          )}
          {checklistItems.map((item, index) => (
            <div key={item._id} className="checklist-item">
              <span className="item-number">{index + 1}.</span>
              {editingId === item._id ? (
                <>
                  <input
                    type="text"
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleUpdateChecklistItem(item._id);
                      if (e.key === 'Escape') {
                        setEditingId(null);
                        setEditingText('');
                      }
                    }}
                    className="item-edit-input"
                    autoFocus
                  />
                  <button className="btn-save-small" onClick={() => handleUpdateChecklistItem(item._id)} title="Save">✓</button>
                  <button className="btn-cancel-small" onClick={() => { setEditingId(null); setEditingText(''); }} title="Cancel">✕</button>
                </>
              ) : (
                <>
                  <span className="item-text">{item.description}</span>
                  <button className="btn-edit-small" onClick={() => { setEditingId(item._id); setEditingText(item.description); }} title="Edit">✏️</button>
                </>
              )}
              <button className="btn-delete-small" onClick={() => handleDeleteChecklistItem(item._id)} title="Delete">×</button>
            </div>
          ))}
        </div>
        <div className="add-section">
          <input
            type="text"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddChecklistItem(); }}
            placeholder="Add new checklist item..."
            className="add-input"
          />
          <button className="btn-add" onClick={handleAddChecklistItem} disabled={!newDescription.trim()}>Add</button>
        </div>
      </div>

      {!hasAnalyzed ? (
        <div className="analysis-prompt">
          <div className="prompt-content">
            <h3>🤖 AI Script Analysis</h3>
            <p>Analyze the script against your checklist criteria to get AI-powered insights and feedback suggestions.</p>
            <button
              className="btn-analyze"
              onClick={analyzeScript}
              disabled={isAnalyzing}
            >
              {isAnalyzing ? (
                <>
                  <span className="spinner"></span>
                  Analyzing...
                </>
              ) : (
                <>
                  🔍 Analyze Script
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="analysis-results">
          <div className="results-header">
            <h3>🤖 AI Script Analysis</h3>
            <div className="overall-score">
              <span>Overall Score:</span>
              <span
                className="score-value"
                style={{ color: getScoreColor(analysisResult?.overallScore || 0) }}
              >
                {analysisResult?.overallScore.toFixed(1)}%
              </span>
            </div>
          </div>

          <div className="checklist-results">
            {analysisResult?.checklistResults && analysisResult.checklistResults.map((result) => (
              <div key={result.id || Math.random()} className="checklist-item-result">
                <div className="result-header">
                  <span className="result-icon">{getCheckIcon(result.passed)}</span>
                  <span className="result-title">{result.description || 'Unknown requirement'}</span>
                </div>
                <div className="result-comment">
                  {result.aiComment || 'No analysis available'}
                </div>
              </div>
            ))}
          </div>

          {analysisResult && analysisResult.suggestedFeedback && (
            <div className="feedback-section">
              <div className="feedback-preview">
                <h4>Suggested Feedback:</h4>
                <div className="feedback-text">
                  {typeof analysisResult.suggestedFeedback === 'string'
                    ? analysisResult.suggestedFeedback
                    : 'Feedback generation failed. Please provide manual feedback.'}
                </div>
              </div>
              <div className="feedback-actions">
                <button
                  className="btn-generate-feedback"
                  onClick={generateFeedback}
                >
                  📝 Use This Feedback
                </button>
                <button
                  className="btn-reanalyze"
                  onClick={analyzeScript}
                  disabled={isAnalyzing}
                >
                  🔄 Analyze Again
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScriptAnalysisPanel;
